// DatePicker.web.js
// Documentation: https://react-day-picker.js.org/guides/input-fields
// IMPORTANT NOTE:
//    UPDATED USEAGE:
//      Do NOT use both `dateSetState` and `onChange`. This has adverse affects.

import React, { useCallback, useRef, useState } from "react";
import { FocusTrap } from "focus-trap-react";
import { DayPicker } from "react-day-picker";
import { usePopper } from "react-popper";
// date-fns focus-trap-react react-day-picker react-popper;

const WebDatePicker = ({dateValue, dateState, dateSetState, openState, openSetState, onChange, onClose, allowBlank, nonWorkDays, ...props})=>{
  const _theme = window.main.theme;
  const isDate = window.main.lib.isDate;
  const isRange = (!!props.mode&&props.mode==="range");

  const [_dateState, _dateSetState] = useState(dateValue||dateState||(!!props.mode&&props.mode==="range"?{from:null,to:null}:(!!allowBlank?null:new Date())));
  dateState = _dateState;
  dateSetState = (dateSetState||_dateSetState);
  const [_openState, _openSetState] = useState(false);
  openState = (openState||_openState);
  openSetState = (openSetState||_openSetState);

  const popperRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);

  const popper = usePopper(popperRef.current, popperElement, { placement: "bottom-start" });

  // Ref can maintain pointer to actual current state and not get cached with the 'closePopper' callback below is created.
  const dateCacheRef = useRef(dateState);
  dateCacheRef.current = dateState;  // reset to *state* on each re-render.

  const closePopper = useCallback(()=>{
    openSetState(false);
    if (typeof(onClose)==="function") {
      onClose(dateCacheRef.current);
    }
  },[dateCacheRef]);

  const handleDaySelect = (date)=>{  // Some examples from https://react-day-picker.js.org/guides/custom-components#wrapping-the-day
    if (!!date) {
      if (!isRange) {
        dateCacheRef.current = new Date(date);
        dateSetState(new Date(date));
        closePopper();
      } else {
        // Using date-range - validate it
        if (!date || !!isDate(date.from) || !isDate(date.to)) {
          date = { from:(!!isDate(date.from)?new Date(date.from):null), to:(!!isDate(date.to)?new Date(date.to):null) };
        }
        if (!!isDate(dateCacheRef.current.to)) {
          dateCacheRef.current.to.setHours(23,59,59);
        }
        // If there were existing dates (BOTH!), and a new/changed 'FROM' is selected, then clear the 'to' date because we are making a NEW range - expecting a second click for the 'to' date.
        let isSecondDate = false;
        if (!!isDate(date.from) && !!isDate(dateCacheRef.current.from) && !!isDate(dateCacheRef.current.to)) {
          if (date.from.toLocaleDateString("sv")===dateCacheRef.current.from.toLocaleDateString("sv") && !!isDate(date.to) && date.to.toLocaleDateString("sv")!==dateCacheRef.current.to.toLocaleDateString("sv")) {
            // Use the *new* selected date from the 'TO' as the new 'FROM' and clear out the 'TO' - expecting a second click for the 'to' date.
            date = { from:new Date(date.to), to:null };
          } else if (date.from.toLocaleDateString("sv")!==dateCacheRef.current.from.toLocaleDateString("sv") && !!isDate(date.to) && date.to.toLocaleDateString("sv")===dateCacheRef.current.to.toLocaleDateString("sv")) {
            // Use the new 'FROM' date and clear the 'TO' - expecting a second click for the 'to' date.
            date = { from:new Date(date.from), to:null };
          }
        } else if (!!isDate(date.to)) {
          isSecondDate = true;
        }
        dateCacheRef.current = { from:(!!isDate(date.from)?new Date(date.from):null), to:(!!isDate(date.to)?new Date(date.to):null) };
        dateSetState({ from:(!!isDate(date.from)?new Date(date.from):null), to:(!!isDate(date.to)?new Date(date.to):null) });
        if (!!isSecondDate) {
          closePopper();
        }
      }
    } else if (!date && !!isDate(dateCacheRef.current.from) && !!isDate(dateCacheRef.current.to)) {
      date = { from:new Date(dateCacheRef.current.from), to:null };
      dateCacheRef.current = date;
      dateSetState({ from:(!!isDate(date.from)?new Date(date.from):null), to:(!!isDate(date.to)?new Date(date.to):null) });
    } else {
      dateCacheRef.current = { from:null, to:null };
      dateSetState({ from:null, to:null });
    }
    // All Cases, always:
    if (typeof(onChange)==="function") {
      onChange(date);
    }
  };

  const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const DOWLabel = (date) => (WEEKDAYS_SHORT[date.getDay()]);

  return (
    <div style={{...props.containerStyle}}>
      <style>{`
        {// Added for Appointments picker to grey out non appointment days available}
        .rdp-disabled .rdp-day_button {
          background-color: #f1f1f1 !important;
          color: #999 !important;
          border: 1px solid #ddd !important;
          cursor: not-allowed !important;
          opacity: 0.65;
        }
        div.rdp {
          display: flex;
        }
        button {
          border-radius: 3px;
          border: 1px solid #808080;
          background-color: ${_theme.colors.monoLight};
        }
        button:hover {
          background-color: ${_theme.colors.monoLight2};
        }
        .rdp-day_selected {
          background-color: ${_theme.colors.mono};
        }
        .rdp-months {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
        }
        ${!isRange ? "" : `
        .rdp-caption_end {
          border-left: 1px solid ${_theme.colors.monoLight2};
          padding-left: 2px;
          margin-left: 2px;
        }`}

        .dialog-sheet {
          inset: -2px auto auto 0px !important;
        }
        .rdp-nav {
          position: absolute;
          top: 4px;
          right: 4px;
          /* display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center; */
          font-size: 0;
          line-height: 0;
          /* color: _theme.colors.black; */
          height: 26px;
        }
        .rdp-month_caption {
          /* display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center; */
          font-size: 20px;
          line-height: 1.3em;
          font-weight: 600;
          /* color: _theme.colors.black; */
          height: 30px;
          padding-left: 6px;
          padding-top: 0px;
        }
        .rdp-button_previous,
        .rdp-button_next {
          height: 28px;
          padding-top: 1px;
        }
        .rdp-weekday {
          font-size: 13px;
          font-weight: 400;
        }
        .rdp-day_button {
          align-self: stretch;
          background-color: ${_theme.colors.monoLight};
          border-radius: 3px;
          border: 1px solid ${_theme.colors.monoMedium2};
          cursor: pointer;
          height: 26px;
          line-height: 9px;
          width: 32px;
        }
        .rdp-selected .rdp-day_button {
          background-color: ${_theme.colors.mode.baseLightHover};
        }
        .rdp-outside .rdp-day_button {
          background-color: ${_theme.colors.monoLight3};
          border: 1px solid ${_theme.colors.mono};
        }
        `}
      </style>
      {!!props.children &&
        <div ref={popperRef} style={{...props.childContainerStyle}}>
          {props.children}
        </div>
      }
      {openState && (
        <FocusTrap
          active={true}
          focusTrapOptions={{
            initialFocus: false,
            clickOutsideDeactivates: true,
            returnFocusOnDeactivate: false,  // ⬅ Without this, clicking outside to deactivate auto-refocuses the same input, flickers, and re-shows the date-picker, which is not what is expected. This is needed.
            escapeDeactivates: true,  // This is not working. Wish it did.
            onDeactivate: closePopper,
          }}
          style={{borderColor:_theme.colors.mono,borderWidth:1,borderRadius:_theme.borderRadius}}
        >
          <div
            ref={setPopperElement}
            role="dialog"
            tabIndex={-1}
            className="dialog-sheet"
            style={{...popper.styles.popper, backgroundColor:_theme.colors.monoBackground,borderColor:_theme.colors.mono,borderWidth:1,borderStyle:"solid",borderRadius:_theme.borderRadius, padding:4, boxShadow:"rgb(0 0 0 / 50%) 1px 4px 14px -4px", shadowRadius: 4, ...props.pickerContainerStyle}}
            {...popper.attributes.popper}
          >
            <DayPicker
              mode={(!!isRange?"range":"single")}
              // initialFocus={openState}
              numberOfMonths={(!!isRange?2:1)}
              defaultMonth={(!!isRange && typeof(dateCacheRef.current)==="object"?dateCacheRef.current?.from:dateCacheRef.current)||new Date()}  // NOTE: if this isn't a real date it will throw confusing error: "Your focus-trap must have at least one container...".
              selected={dateCacheRef.current}
              onSelect={handleDaySelect}
              formatters={{ formatWeekdayName:DOWLabel }}
              showOutsideDays={true}
              {...props}
            />
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

export default WebDatePicker;
